import { PositionType } from 'data/types';
import { polygonBoundingBox } from 'utils/footprint';
import { rotateFootprintPointsAboutCentroid } from 'utils/footprint-shapes-geometry';

export type FootprintShapeVariantId =
  | 'square'
  | 'rectangle-landscape'
  | 'rectangle-portrait'
  | 'l-shape-0'
  | 'l-shape-90'
  | 'l-shape-180'
  | 'l-shape-270'
  | 't-shape-0'
  | 't-shape-90'
  | 't-shape-180'
  | 't-shape-270'
  | 'u-shape-0'
  | 'u-shape-90'
  | 'u-shape-180'
  | 'u-shape-270';

export type FootprintCategoryId = 'common' | 'uncommon';

export type FootprintTemplate = {
  id: FootprintShapeVariantId;
  label: string;
  previewPoints: string;
};

export type FootprintCategory = {
  id: FootprintCategoryId;
  label: string;
  templates: FootprintTemplate[];
};

type RotatableShapeBase = 'l-shape' | 't-shape' | 'u-shape';

export const FOOTPRINT_SHAPE_PREVIEW_SIZE = 40;
export const DEFAULT_PRESET_AREA_SQFT = 900;

const MIN_TARGET_AREA_SQFT = 100;
const RECTANGLE_ASPECT_RATIO = 1.5;

const ROTATION_VARIANT_IDS: Record<
  RotatableShapeBase,
  [
    FootprintShapeVariantId,
    FootprintShapeVariantId,
    FootprintShapeVariantId,
    FootprintShapeVariantId
  ]
> = {
  'l-shape': ['l-shape-0', 'l-shape-90', 'l-shape-180', 'l-shape-270'],
  't-shape': ['t-shape-0', 't-shape-90', 't-shape-180', 't-shape-270'],
  'u-shape': ['u-shape-0', 'u-shape-90', 'u-shape-180', 'u-shape-270']
};

const ROTATION_DEGREES = [0, 90, 180, 270] as const;

const SQUARE_PREVIEW: PositionType[] = [
  { x: 0, y: 0 },
  { x: 10, y: 0 },
  { x: 10, y: 10 },
  { x: 0, y: 10 }
];

const RECTANGLE_LANDSCAPE_PREVIEW: PositionType[] = [
  { x: 0, y: 0 },
  { x: 14, y: 0 },
  { x: 14, y: 9 },
  { x: 0, y: 9 }
];

const RECTANGLE_PORTRAIT_PREVIEW: PositionType[] = [
  { x: 0, y: 0 },
  { x: 9, y: 0 },
  { x: 9, y: 14 },
  { x: 0, y: 14 }
];

const L_SHAPE_PREVIEW: PositionType[] = [
  { x: 0, y: 0 },
  { x: 10, y: 0 },
  { x: 10, y: 5 },
  { x: 5, y: 5 },
  { x: 5, y: 10 },
  { x: 0, y: 10 }
];

const T_SHAPE_PREVIEW: PositionType[] = [
  { x: 0, y: 0 },
  { x: 12, y: 0 },
  { x: 12, y: 4 },
  { x: 8, y: 4 },
  { x: 8, y: 10 },
  { x: 4, y: 10 },
  { x: 4, y: 4 },
  { x: 0, y: 4 }
];

const U_SHAPE_PREVIEW: PositionType[] = [
  { x: 0, y: 0 },
  { x: 12, y: 0 },
  { x: 12, y: 10 },
  { x: 9, y: 10 },
  { x: 9, y: 4 },
  { x: 3, y: 4 },
  { x: 3, y: 10 },
  { x: 0, y: 10 }
];

function footprintPolygonArea(points: PositionType[]): number {
  let area = 0;
  for (let i = 0, j = points.length - 1; i < points.length; j = i, i += 1) {
    area += points[j].x * points[i].y - points[i].x * points[j].y;
  }
  return Math.abs(area) / 2;
}

function centerFootprintPoints(points: PositionType[]): PositionType[] {
  const cx = points.reduce((sum, point) => sum + point.x, 0) / points.length;
  const cy = points.reduce((sum, point) => sum + point.y, 0) / points.length;
  const offsetX = Math.round(cx);
  const offsetY = Math.round(cy);
  return points.map((point) => ({
    x: point.x - offsetX,
    y: point.y - offsetY
  }));
}

function scaleFootprintToTargetArea(
  points: PositionType[],
  targetArea: number
): PositionType[] {
  const actual = footprintPolygonArea(points);
  if (actual <= 0) return points;
  const scale = Math.sqrt(targetArea / actual);
  if (Math.abs(scale - 1) < 0.001) return points;
  const cx = points.reduce((sum, point) => sum + point.x, 0) / points.length;
  const cy = points.reduce((sum, point) => sum + point.y, 0) / points.length;
  return points.map((point) => ({
    x: Math.round(cx + (point.x - cx) * scale),
    y: Math.round(cy + (point.y - cy) * scale)
  }));
}

function templatePreviewPointsAttr(
  previewPoints: PositionType[],
  padding = 4
): string {
  const viewSize = FOOTPRINT_SHAPE_PREVIEW_SIZE;
  const bounds = polygonBoundingBox(previewPoints);
  const width = bounds.maxX - bounds.minX || 1;
  const height = bounds.maxY - bounds.minY || 1;
  const scale = (viewSize - padding * 2) / Math.max(width, height);
  const offsetX = (viewSize - width * scale) / 2;
  const offsetY = (viewSize - height * scale) / 2;
  return previewPoints
    .map(
      (point) =>
        `${(point.x - bounds.minX) * scale + offsetX},${
          (point.y - bounds.minY) * scale + offsetY
        }`
    )
    .join(' ');
}

function rotationTemplates(
  base: RotatableShapeBase,
  label: string,
  preview: PositionType[]
): FootprintTemplate[] {
  return ROTATION_VARIANT_IDS[base].map((id, index) => {
    const degrees = ROTATION_DEGREES[index];
    return {
      id,
      label: degrees === 0 ? label : `${label} ${degrees}\u00b0`,
      previewPoints: templatePreviewPointsAttr(
        degrees === 0
          ? preview
          : rotateFootprintPointsAboutCentroid(preview, degrees)
      )
    };
  });
}

function generateSquare(area: number): PositionType[] {
  const side = Math.round(Math.sqrt(area));
  return centerFootprintPoints([
    { x: 0, y: 0 },
    { x: side, y: 0 },
    { x: side, y: side },
    { x: 0, y: side }
  ]);
}

function generateLandscapeRectangle(area: number): PositionType[] {
  const width = Math.round(Math.sqrt(area * RECTANGLE_ASPECT_RATIO));
  const height = Math.max(1, Math.round(area / width));
  return centerFootprintPoints([
    { x: 0, y: 0 },
    { x: width, y: 0 },
    { x: width, y: height },
    { x: 0, y: height }
  ]);
}

function generatePortraitRectangle(area: number): PositionType[] {
  const height = Math.round(Math.sqrt(area * RECTANGLE_ASPECT_RATIO));
  const width = Math.max(1, Math.round(area / height));
  return centerFootprintPoints([
    { x: 0, y: 0 },
    { x: width, y: 0 },
    { x: width, y: height },
    { x: 0, y: height }
  ]);
}

function generateLShape(area: number): PositionType[] {
  const boundingSide = Math.round(Math.sqrt((area * 4) / 3));
  const half = Math.round(boundingSide / 2);
  return centerFootprintPoints([
    { x: 0, y: 0 },
    { x: boundingSide, y: 0 },
    { x: boundingSide, y: half },
    { x: half, y: half },
    { x: half, y: boundingSide },
    { x: 0, y: boundingSide }
  ]);
}

function generateTShape(area: number): PositionType[] {
  const totalW = Math.round(Math.sqrt(area * 2));
  const totalH = Math.round(area / (totalW * 0.75));
  const stemW = Math.round(totalW / 3);
  const barH = Math.round(totalH * 0.4);
  const stemLeft = Math.round((totalW - stemW) / 2);
  return centerFootprintPoints([
    { x: 0, y: 0 },
    { x: totalW, y: 0 },
    { x: totalW, y: barH },
    { x: stemLeft + stemW, y: barH },
    { x: stemLeft + stemW, y: totalH },
    { x: stemLeft, y: totalH },
    { x: stemLeft, y: barH },
    { x: 0, y: barH }
  ]);
}

function generateUShape(area: number): PositionType[] {
  const totalW = Math.round(Math.sqrt(area * 1.25));
  const totalH = Math.max(1, Math.round(area / (totalW * 0.75)));
  const wallThickness = Math.max(1, Math.round(totalW / 4));
  const notchH = Math.max(1, Math.round(totalH * 0.6));
  return centerFootprintPoints([
    { x: 0, y: 0 },
    { x: totalW, y: 0 },
    { x: totalW, y: totalH },
    { x: totalW - wallThickness, y: totalH },
    { x: totalW - wallThickness, y: totalH - notchH },
    { x: wallThickness, y: totalH - notchH },
    { x: wallThickness, y: totalH },
    { x: 0, y: totalH }
  ]);
}

function generateTShapeNormalized(area: number): PositionType[] {
  return centerFootprintPoints(
    scaleFootprintToTargetArea(generateTShape(area), area)
  );
}

function generateUShapeNormalized(area: number): PositionType[] {
  return centerFootprintPoints(
    scaleFootprintToTargetArea(generateUShape(area), area)
  );
}

export function generateFootprintShape(
  variantId: FootprintShapeVariantId,
  targetArea: number
): PositionType[] {
  const safeArea = Math.max(MIN_TARGET_AREA_SQFT, targetArea);
  const generators: Record<FootprintShapeVariantId, () => PositionType[]> = {
    square: () => generateSquare(safeArea),
    'rectangle-landscape': () => generateLandscapeRectangle(safeArea),
    'rectangle-portrait': () => generatePortraitRectangle(safeArea),
    'l-shape-0': () => generateLShape(safeArea),
    'l-shape-90': () =>
      rotateFootprintPointsAboutCentroid(generateLShape(safeArea), 90),
    'l-shape-180': () =>
      rotateFootprintPointsAboutCentroid(generateLShape(safeArea), 180),
    'l-shape-270': () =>
      rotateFootprintPointsAboutCentroid(generateLShape(safeArea), 270),
    't-shape-0': () => generateTShapeNormalized(safeArea),
    't-shape-90': () =>
      rotateFootprintPointsAboutCentroid(
        generateTShapeNormalized(safeArea),
        90
      ),
    't-shape-180': () =>
      rotateFootprintPointsAboutCentroid(
        generateTShapeNormalized(safeArea),
        180
      ),
    't-shape-270': () =>
      rotateFootprintPointsAboutCentroid(
        generateTShapeNormalized(safeArea),
        270
      ),
    'u-shape-0': () => generateUShapeNormalized(safeArea),
    'u-shape-90': () =>
      rotateFootprintPointsAboutCentroid(
        generateUShapeNormalized(safeArea),
        90
      ),
    'u-shape-180': () =>
      rotateFootprintPointsAboutCentroid(
        generateUShapeNormalized(safeArea),
        180
      ),
    'u-shape-270': () =>
      rotateFootprintPointsAboutCentroid(
        generateUShapeNormalized(safeArea),
        270
      )
  };
  return centerFootprintPoints(
    scaleFootprintToTargetArea(generators[variantId](), safeArea)
  );
}

export const FOOTPRINT_TEMPLATE_CATEGORIES: FootprintCategory[] = [
  {
    id: 'common',
    label: 'Common Shapes',
    templates: [
      {
        id: 'square',
        label: 'Square',
        previewPoints: templatePreviewPointsAttr(SQUARE_PREVIEW)
      },
      {
        id: 'rectangle-landscape',
        label: 'Rectangle',
        previewPoints: templatePreviewPointsAttr(RECTANGLE_LANDSCAPE_PREVIEW)
      },
      {
        id: 'rectangle-portrait',
        label: 'Tall rectangle',
        previewPoints: templatePreviewPointsAttr(RECTANGLE_PORTRAIT_PREVIEW)
      },
      ...rotationTemplates('l-shape', 'L shape', L_SHAPE_PREVIEW)
    ]
  },
  {
    id: 'uncommon',
    label: 'Uncommon Shapes',
    templates: [
      ...rotationTemplates('t-shape', 'T shape', T_SHAPE_PREVIEW),
      ...rotationTemplates('u-shape', 'U shape', U_SHAPE_PREVIEW)
    ]
  }
];

export const footprintTemplates: FootprintTemplate[] =
  FOOTPRINT_TEMPLATE_CATEGORIES.flatMap((category) => category.templates);
